import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  ListItemSecondaryAction,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  LinearProgress,
  Tooltip
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axios';
import { useAuthAwareSnackbar } from '../../../hooks/useAuthErrorHandler';
import { useAuth } from '../../../contexts/AuthContext';
import { Close as CloseIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';
import Autocomplete from '@mui/material/Autocomplete';
import { FILE_BASE_URL } from '../../../config';
import RelatedIncomingDocuments from './RelatedIncomingDocuments';
import RecipientDetailsDialog, { RecipientDetailsData } from './RecipientDetailsDialog';
import SendToContractorsAfterPublishModal from './SendToContractorsAfterPublishModal';

interface CreateDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isEdit?: boolean;
  initialData?: DocumentFormData;
  documentId?: number;
  fromTask?: boolean;
  taskId?: string;
}

interface FileAttachment {
  id: number;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  description: string;
  file_type_category?: string | null;
}

// Interface cho dữ liệu file từ API
interface ApiFileData {
  id: number;
  filename: string; // API sử dụng 'filename' thay vì 'file_name'
  file_path: string;
  file_type: string;
  file_size: number;
  description?: string;
  file_category?: string; // Backend mới trả về 'file_category'
  file_type_category?: string; // Fallback cho interface cũ
  upload_date?: string;
  uploaded_by?: string;
  uploaded_by_fullname?: string;
  fileview_active?: string;
  preview_file_path?: string;
  is_preview_ready?: boolean;
  is_converting?: boolean;
  kyso_file_info?: any;
}

interface DocumentFormData {
  document_number: string;
  document_code: string;
  title: string;
  document_date: string;
  sender: string;
  recipient: string;
  document_type: string;
  priority: string;
  security_level: string;
  description: string;
  status: string;
  tags: string[];
  leader_phong_id: number | null;
  leader_ban_id: number | null;
  files?: (FileAttachment | ApiFileData)[];
  han_xu_ly?: string;
  don_vi_soan_thao?: string;
  don_vi_ban_hanh?: string;
  xu_ly_chinh_mrb: string;
  phoi_hop_mrb: string[];
  don_vi_xu_ly: string[];
  don_vi_xu_ly_cp: string[];
  don_vi_phoi_hop_chua_qlvb: Array<{chuc_nang: string, don_vi: string}>;
  ghi_chu?: string;
  luong_xanh?: string;
  sokyhieu_flyid_den?: Array<{
    id_incoming: string;
    so_ky_hieu_den: string;
    flyID_den: string;
  }>;
}

interface User {
  id: number;
  fullname: string;
  username: string;
  roles: string[];
  position?: string; // Thêm trường position để phân biệt trưởng phòng và phó trưởng phòng
}

interface NoiNhanItem {
  data_value: string;
  id: number;
  noi_nhan_name: string;
  text: string;
  value: string;
}

interface FileData {
  id?: number;
  file_name: string;
  file_type: string;
  file_size: number;
  file_path: string;
  description: string;
  file_type_category?: string | null;
  file?: File; // Thêm trường file để lưu File object
}

interface UploadProgress {
  [key: string]: number; // key là file name, value là phần trăm upload (0-100)
}

interface DonViItem {
  id: number;
  code: string;
  name: string;
  short_name: string | null;
  Don_vi_cap: number;
}

interface ExistingNhaThauRecord {
  id: number;
  name_nha_thau: string;
  fly_id: number | null;
  id_out: number | null;
  gui_mail_check: number;
}

// Helper function để format file size an toàn
const formatFileSize = (size?: number | null): string => {
  if (!size || isNaN(size) || size <= 0) return '0 KB';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const DON_VI_SOAN_THAO_OPTIONS = [
  'Ban Quản lý Đường sắt đô thị',
  'Phòng Hành chính Tổ chức',
  'Phòng Tài chính Kế toán',
  'Phòng Kế hoạch Tổng hợp',
  'Phòng Kỹ thuật Thẩm định',
  'Quản lý thực hiện dự án 1',
  'Quản lý thực hiện dự án 2',
  'Quản lý thực hiện dự án 3',
  'Quản lý thực hiện dự án 4',
];

// Thêm hằng số kích thước file tối đa (50MB)
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes

// Thêm hằng số kích thước chunk
const CHUNK_SIZE = 200 * 1024; // 200KB

// Số lượng file chính tối đa
const MAX_MAIN_FILES = 28;

// Các extension file được phép cho file chính
const ALLOWED_MAIN_FILE_EXTENSIONS = ['.doc', '.docx', '.xls', '.xlsx', '.pdf'];

// Các extension file được phép cho file liên quan (thêm ảnh)
const ALLOWED_REFERENCE_FILE_EXTENSIONS = ['.doc', '.docx', '.xls', '.xlsx', '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];

// Hàm kiểm tra extension file
const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) return '';
  return fileName.substring(lastDotIndex).toLowerCase();
};

// Hàm kiểm tra file có được phép upload không (cho file chính)
const isMainFileAllowed = (fileName: string): boolean => {
  const extension = getFileExtension(fileName);
  return ALLOWED_MAIN_FILE_EXTENSIONS.includes(extension);
};

// Hàm kiểm tra file có được phép upload không (cho file liên quan)
const isReferenceFileAllowed = (fileName: string): boolean => {
  const extension = getFileExtension(fileName);
  return ALLOWED_REFERENCE_FILE_EXTENSIONS.includes(extension);
};

// Hàm chia file thành các chunks
const createChunks = (file: File, chunkSize = CHUNK_SIZE) => {
  const chunks: Blob[] = [];
  let start = 0;
  
  while (start < file.size) {
    const chunk = file.slice(start, start + chunkSize);
    chunks.push(chunk);
    start += chunkSize;
  }
  
  return chunks;
};

// Hàm chuyển blob thành base64
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      resolve(base64.split(',')[1]); // Lấy phần sau dấu phẩy (bỏ data:image/png;base64,)
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Hàm upload một chunk
const uploadChunk = async (
  file: File,
  chunk: Blob,
  chunkNumber: number,
  totalChunks: number,
  documentData: any,
  fileTypeCategory: string
) => {
  // Chuyển chunk thành base64
  const base64Chunk = await blobToBase64(chunk);
  
  // Chuẩn bị payload
  const payload = {
    data: documentData,
    file_info: {
      file_name: file.name,
      file_type: file.type,
      file_type_category: fileTypeCategory,
      description: '',
      total_chunks: totalChunks,
      chunk_number: chunkNumber,
      file_size: file.size,
      chunk_data: base64Chunk
    }
  };

  const response = await axiosInstance.post('/outgoing-documents', payload);
  return response.data;
};

// Hàm upload file với progress
const uploadFileWithChunks = async (
  file: File,
  documentData: any,
  fileTypeCategory: string,
  onProgress: (progress: number) => void
) => {
  try {
    // Chia file thành chunks
    const chunks = createChunks(file);
    const totalChunks = chunks.length;

    // Upload từng chunk
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      
      const result = await uploadChunk(file, chunk, i, totalChunks, documentData, fileTypeCategory);
      
      // Tính và báo cáo progress
      const progress = ((i + 1) / totalChunks) * 100;
      onProgress(progress);

      // Nếu là chunk cuối cùng, trả về kết quả
      if (i === totalChunks - 1) {
        return result;
      }
    }

  } catch (error) {

    throw error;
  }
};

const CreateDocumentDialog: React.FC<CreateDocumentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  isEdit,
  initialData,
  documentId,
  fromTask,
  taskId
}) => {
  // QUAN TRỌNG: Khai báo authentication state TRƯỚC TIÊN để tránh lỗi "can't access before initialization"
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { enqueueSnackbar } = useAuthAwareSnackbar();
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [truongPhongList, setTruongPhongList] = useState<User[]>([]);
  const [lanhDaoBanList, setLanhDaoBanList] = useState<User[]>([]);
  const [soanThaoList, setSoanThaoList] = useState<User[]>([]);
  const [noiNhanList, setNoiNhanList] = useState<NoiNhanItem[]>([]);
  const [donViXuLyList, setDonViXuLyList] = useState<NoiNhanItem[]>([]);
  const [donViXuLyCPList, setDonViXuLyCPList] = useState<NoiNhanItem[]>([]);
  const [donViChuaQLVBList, setDonViChuaQLVBList] = useState<NoiNhanItem[]>([]);
  const [donViList, setDonViList] = useState<DonViItem[]>([]);
  const [selectedNhaThau, setSelectedNhaThau] = useState<string[]>([]);
  const [existingNhaThauRecords, setExistingNhaThauRecords] = useState<ExistingNhaThauRecord[]>([]);
  const [formData, setFormData] = useState<DocumentFormData>({
    document_number: initialData?.document_number || '',
    document_code: initialData?.document_code || '',
    title: initialData?.title || '',
    document_date: initialData?.document_date || new Date().toISOString().split('T')[0],
    sender: initialData?.sender || '',
    recipient: initialData?.recipient || '',
    document_type: initialData?.document_type || 'Công văn',
    priority: initialData?.priority || 'Thường',
    security_level: initialData?.security_level || 'Thường',
    description: initialData?.description || '',
    status: initialData?.status || 'moi_khoi_tao',
    tags: initialData?.tags || [],
    leader_phong_id: initialData?.leader_phong_id || null,
    leader_ban_id: initialData?.leader_ban_id || null,
    han_xu_ly: initialData?.han_xu_ly || '',
    don_vi_soan_thao: initialData?.don_vi_ban_hanh || initialData?.don_vi_soan_thao || 'Ban Quản lý Đường sắt đô thị',
    xu_ly_chinh_mrb: '',
    phoi_hop_mrb: [],
    don_vi_xu_ly: [],
    don_vi_xu_ly_cp: [],
    don_vi_phoi_hop_chua_qlvb: [],
    ghi_chu: '',
    luong_xanh: initialData?.luong_xanh || '',
  });

  const [selectedFiles, setSelectedFiles] = useState<FileData[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingFileIndex, setPendingFileIndex] = useState(0);
  const [pendingFileBase64, setPendingFileBase64] = useState<string>('');
  const [showFileTypeDialog, setShowFileTypeDialog] = useState(false);
  const [currentFileType, setCurrentFileType] = useState<'main' | 'reference'>('main');
  
  // Drag & drop states
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragFiles, setDragFiles] = useState<File[]>([]);
  const [showRelatedDocsDialog, setShowRelatedDocsDialog] = useState(false);
  const [showRecipientDialog, setShowRecipientDialog] = useState(false);
  // State để lưu trữ tạm thời các văn bản đến liên quan đã chọn
  const [relatedIncomingDocs, setRelatedIncomingDocs] = useState<Array<{
    id_incoming: string;
    so_ky_hieu_den: string;
    flyID_den: string;
  }>>([]);
  // State để lưu trữ thông tin chi tiết của các văn bản đến liên quan
  const [relatedIncomingDocsDetails, setRelatedIncomingDocsDetails] = useState<Array<{
    id_incoming: string;
    so_ky_hieu_den: string;
    flyID_den: string;
    document_code: string;
    description: string;
  }>>([]);
  // State cho modal xác nhận trình lãnh đạo phòng
  const [confirmTrinhLanhDaoPhongOpen, setConfirmTrinhLanhDaoPhongOpen] = useState(false);
  const [pendingDocumentId, setPendingDocumentId] = useState<number | null>(null);
  const [trinhLanhDaoPhongLoading, setTrinhLanhDaoPhongLoading] = useState(false);
  
  // State cho modal xác nhận kết thúc văn bản đến
  const [confirmKetThucVanBanDenOpen, setConfirmKetThucVanBanDenOpen] = useState(false);
  const [ketThucVanBanDenLoading, setKetThucVanBanDenLoading] = useState(false);
  const [pendingNewDocumentId, setPendingNewDocumentId] = useState<number | null>(null);

  /** Sau tạo mới: hỏi gửi cho nhà thầu nào khi ban hành */
  const [sendToContractorsAfterCreateOpen, setSendToContractorsAfterCreateOpen] = useState(false);
  const [pendingDocIdForContractors, setPendingDocIdForContractors] = useState<number | null>(null);

  // Hàm xử lý khi save từ RecipientDetailsDialog
  const handleSaveRecipientDetails = (data: RecipientDetailsData) => {
    setFormData(prev => ({
      ...prev,
      recipient: data.recipient,
      xu_ly_chinh_mrb: data.xu_ly_chinh_mrb,
      phoi_hop_mrb: data.phoi_hop_mrb,
      don_vi_xu_ly: data.don_vi_xu_ly,
      don_vi_xu_ly_cp: data.don_vi_xu_ly_cp,
      don_vi_phoi_hop_chua_qlvb: data.don_vi_phoi_hop_chua_qlvb,
      description: data.description,
      ghi_chu: data.ghi_chu
    }));
  };

  // Hàm để fetch thông tin chi tiết của văn bản đến liên quan
  const fetchRelatedIncomingDocsDetails = async (relatedDocs: Array<{ id_incoming: string; so_ky_hieu_den: string; flyID_den: string; }>) => {
    try {
      const detailsPromises = relatedDocs.map(async (doc) => {
        try {
          // Sử dụng API đúng: /xlvb/incoming-documents/chitiet-vanban?doc_id=
          const response = await axiosInstance.get('/xlvb/incoming-documents/chitiet-vanban', {
            params: {
              doc_id: doc.id_incoming
            }
          });
          
          if (response.data.code === 0) {
            const data = response.data.data.document || response.data.data;
            
            return {
              id_incoming: doc.id_incoming,
              so_ky_hieu_den: doc.so_ky_hieu_den,
              flyID_den: doc.flyID_den,
              document_code: data.document_code || data.document_number || '',
              description: data.description || data.title || ''
            };
          }
          return null;
        } catch (error) {

          return null;
        }
      });

      const details = await Promise.all(detailsPromises);
      const validDetails = details.filter((detail): detail is {
        id_incoming: string;
        so_ky_hieu_den: string;
        flyID_den: string;
        document_code: string;
        description: string;
      } => detail !== null);
      setRelatedIncomingDocsDetails(validDetails);
    } catch (error) {

    }
  };

  // Mapping đơn vị để chuyển đổi tên đơn vị thành mã đơn vị
  const unitMapping = {
    'Ban Quản lý Đường sắt đô thị': 'BAN_QL',
    'Phòng Hành chính Tổ chức': 'HCTC',
    'Phòng Tài chính Kế toán': 'TCKT',
    'Phòng Kế hoạch Tổng hợp': 'KHTH',
    'Phòng Kỹ thuật Thẩm định': 'KT_TD',
    'Quản lý thực hiện dự án 1': 'DA1',
    'Quản lý thực hiện dự án 2': 'DA2',
    'Quản lý thực hiện dự án 3': 'DA3',
    'Quản lý thực hiện dự án 4': 'DA4',
  };

  // Hàm lấy danh sách nơi nhận từ API
  const fetchNoiNhanList = async () => {
    try {
      const response = await axiosInstance.post('/noi-nhan', {
        noi_nhan_name: "MRB",
        use_paginate: false
      });

      if (response.data.code === 0) {
        setNoiNhanList(response.data.data.items || []);
      } else {
        setNoiNhanList([]);
        enqueueSnackbar('Không thể tải danh sách nơi nhận', { variant: 'error' });
      }
    } catch (error) {

      setNoiNhanList([]);
      enqueueSnackbar('Không thể tải danh sách nơi nhận', { variant: 'error' });
    }
  };

  // Hàm lấy danh sách đơn vị xử lý sở ban ngành quận huyện từ API
  const fetchDonViXuLyList = async () => {
    try {
      const response = await axiosInstance.post('/noi-nhan', {
        noi_nhan_name: "sobannganhquanhuyen",
        use_paginate: false
      });

      if (response.data.code === 0) {
        setDonViXuLyList(response.data.data.items || []);
      } else {
        setDonViXuLyList([]);
        enqueueSnackbar('Không thể tải danh sách đơn vị xử lý', { variant: 'error' });
      }
    } catch (error) {

      setDonViXuLyList([]);
      enqueueSnackbar('Không thể tải danh sách đơn vị xử lý', { variant: 'error' });
    }
  };

  // Hàm lấy danh sách đơn vị xử lý Chính phủ/Bộ ngành Trung ương từ API
  const fetchDonViXuLyCPList = async () => {
    try {
      const response = await axiosInstance.post('/noi-nhan', {
        noi_nhan_name: "chinhphubonganhtrunguong",
        use_paginate: false
      });

      if (response.data.code === 0) {
        setDonViXuLyCPList(response.data.data.items || []);
      } else {
        setDonViXuLyCPList([]);
        enqueueSnackbar('Không thể tải danh sách đơn vị xử lý Chính phủ/Bộ ngành', { variant: 'error' });
      }
    } catch (error) {

      setDonViXuLyCPList([]);
      enqueueSnackbar('Không thể tải danh sách đơn vị xử lý Chính phủ/Bộ ngành', { variant: 'error' });
    }
  };

  // Hàm lấy danh sách đơn vị chưa sử dụng QLVB tập trung từ API
  const fetchDonViChuaQLVBList = async () => {
    try {
      const response = await axiosInstance.post('/noi-nhan', {
        noi_nhan_name: "donvichuasudungqlvb",
        use_paginate: false
      });

      if (response.data.code === 0) {
        setDonViChuaQLVBList(response.data.data.items || []);
      } else {
        setDonViChuaQLVBList([]);
        enqueueSnackbar('Không thể tải danh sách đơn vị chưa sử dụng QLVB tập trung', { variant: 'error' });
      }
    } catch (error) {

      setDonViChuaQLVBList([]);
      enqueueSnackbar('Không thể tải danh sách đơn vị chưa sử dụng QLVB tập trung', { variant: 'error' });
    }
  };

  // Hàm lấy danh sách đơn vị (donvi) cho nhà thầu ban hành
  const fetchDonViList = async () => {
    try {
      const response = await axiosInstance.get('/donvi', {
        params: { page: 1, page_size: -1 }
      });
      if (response.data.code === 0) {
        setDonViList(response.data.data.records || []);
      } else {
        setDonViList([]);
      }
    } catch (error) {
      setDonViList([]);
    }
  };

  // Hàm lấy nhà thầu ban hành đã lưu theo id_out (dùng khi chỉnh sửa)
  const fetchExistingNhaThauByIdOut = async (idOut: number) => {
    try {
      const response = await axiosInstance.get(`/tuvan-nhathau/id-out/${idOut}/nha-thau-ban-hanh`);
      if (response.data.code === 0 && response.data.data) {
        const records: ExistingNhaThauRecord[] = response.data.data;
        setExistingNhaThauRecords(records);
        setSelectedNhaThau(records.map(r => r.name_nha_thau).filter(Boolean) as string[]);
      }
    } catch (error) {
      setExistingNhaThauRecords([]);
    }
  };

  // Hàm lấy danh sách người soạn thảo theo đơn vị
  const fetchSoanThaoList = async (donViSoanThao: string) => {
    // QUAN TRỌNG: Chỉ gọi API sau khi đã authenticated
    if (!isAuthenticated || authLoading) {
      return;
    }
    
    if (!donViSoanThao) {
      setSoanThaoList([]);
      return;
    }

    try {
      const unitCode = unitMapping[donViSoanThao as keyof typeof unitMapping];
      if (!unitCode) {
        setSoanThaoList([]);
        return;
      }

      const response = await axiosInstance.post('/users/by-unit', {
        don_vi: unitCode,
        keyword: ''
      });

      if (response.data.code === 0) {
        setSoanThaoList(response.data.data.records);
      } else {
        setSoanThaoList([]);
        enqueueSnackbar('Không thể tải danh sách người soạn thảo', { variant: 'error' });
      }
    } catch (error) {

      setSoanThaoList([]);
      // Chỉ hiển thị error nếu đã authenticated
      if (isAuthenticated) {
        enqueueSnackbar('Không thể tải danh sách người soạn thảo', { variant: 'error' });
      }
    }
  };

  useEffect(() => {
    if (!open) {
      setSendToContractorsAfterCreateOpen(false);
      setPendingDocIdForContractors(null);
    }
  }, [open]);

  // Effect để lấy danh sách nơi nhận khi dialog mở
  useEffect(() => {
    if (open) {
      fetchNoiNhanList();
      fetchDonViXuLyList();
      fetchDonViXuLyCPList();
      fetchDonViChuaQLVBList();
      fetchDonViList();

      // Khi chỉnh sửa, load nhà thầu ban hành đã lưu
      if (isEdit && documentId) {
        fetchExistingNhaThauByIdOut(documentId);
      } else {
        setSelectedNhaThau([]);
        setExistingNhaThauRecords([]);
      }
    }
  }, [open]);

  // Effect để lấy danh sách người soạn thảo khi đơn vị soạn thảo thay đổi
  useEffect(() => {
    // QUAN TRỌNG: Chỉ gọi API sau khi đã authenticated
    if (authLoading || !isAuthenticated) {
      return;
    }
    
    if (formData.don_vi_soan_thao) {
      fetchSoanThaoList(formData.don_vi_soan_thao);
    } else {
      setSoanThaoList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.don_vi_soan_thao, isAuthenticated, authLoading]);

  useEffect(() => {
    if (initialData?.files) {
      
      const existingFiles: FileData[] = initialData.files.map(file => {
        // Xử lý dữ liệu từ API có thể có cấu trúc khác
        const isApiFileData = 'filename' in file;
        
        
        const mappedFile = {
          file_name: isApiFileData ? (file as ApiFileData).filename : (file as FileAttachment).file_name,
          file_type: file.file_type || '',
          file_size: file.file_size || 0,
          file_path: file.file_path,
          description: file.description || '',
          file_type_category: (() => {
            // Backend đã cập nhật! Ưu tiên file_category
            if ((file as any).file_category && !(file as any).file_category.includes('/')) {
              return (file as any).file_category;
            }
            // Fallback file_type_category nếu không phải MIME type
            if (file.file_type_category && !file.file_type_category.includes('/')) {
              return file.file_type_category;
            }
            // Nếu vẫn là MIME type (cache issue), mặc định 'main'
            return 'main';
          })(),
          file: undefined, // Không lưu File object cho file đã tồn tại
          id: file.id // Thêm id để phân biệt file cũ
        };
        
        return mappedFile;
      });
      
      setSelectedFiles(existingFiles);
    }
  }, [initialData, isEdit]);

  useEffect(() => {
    const fetchLeaders = async () => {
      if (open) {
        try {
          const [usersResponse, trinhLdPhongResponse] = await Promise.all([
            axiosInstance.get('/users', { params: { page_size: -1 } }),
            axiosInstance.get('/xlvb/outgoing-documents/users-trinh-lanhdao-phong'),
          ]);

          if (usersResponse.data.code === 0) {
            const allUsers = usersResponse.data.data?.records || usersResponse.data.data || [];

            let truongPhongsFromApi: User[] =
              trinhLdPhongResponse.data?.code === 0
                ? trinhLdPhongResponse.data.data?.users || []
                : [];

            // API lỗi → lọc client như cũ (chỉ TRUONG_PHONG cùng phòng, không có user_kyso_replace)
            if (truongPhongsFromApi.length === 0 && trinhLdPhongResponse.data?.code !== 0) {
              const currentUserStr = localStorage.getItem('currentUser');
              let currentUserDepartment = '';
              if (currentUserStr) {
                try {
                  const currentUser = JSON.parse(currentUserStr);
                  currentUserDepartment = currentUser.department || currentUser.Don_vi?.[0] || '';
                } catch (e) {
                  /* ignore */
                }
              }
              truongPhongsFromApi = allUsers.filter((u: User) => {
                if (!u.roles?.includes('TRUONG_PHONG')) return false;
                if (currentUserDepartment) {
                  const userDepartment = (u as any).department || (u as any).Don_vi?.[0] || '';
                  return userDepartment === currentUserDepartment;
                }
                return true;
              });
            }

            const truongPhong = truongPhongsFromApi.filter(
              (user: User) => (user.position || '').trim().toLowerCase() === 'trưởng phòng'
            );
            const phoTruongPhong = truongPhongsFromApi.filter(
              (user: User) => (user.position || '').trim().toLowerCase() === 'phó trưởng phòng'
            );
            const otherTruongPhong = truongPhongsFromApi.filter(
              (user: User) =>
                (user.position || '').trim().toLowerCase() !== 'trưởng phòng' &&
                (user.position || '').trim().toLowerCase() !== 'phó trưởng phòng'
            );
            const sortedTruongPhongs = [...truongPhong, ...phoTruongPhong, ...otherTruongPhong];

            const lanhDaoBans = allUsers.filter((user: User) => user.roles.includes('LANH_DAO_BAN'));

            let finalTruongPhongList = [...sortedTruongPhongs];
            let finalLanhDaoBanList = [...lanhDaoBans];

            if (isEdit && initialData) {
              if (initialData.leader_phong_id) {
                const foundInList = sortedTruongPhongs.find(
                  (user: User) => user.id === initialData.leader_phong_id
                );
                if (!foundInList) {
                  const selectedUser = allUsers.find((user: User) => user.id === initialData.leader_phong_id);
                  if (selectedUser) {
                    finalTruongPhongList = [selectedUser, ...sortedTruongPhongs];
                  }
                }
              }
              
              // Xử lý cho lãnh đạo ban ký
              if (initialData.leader_ban_id) {
                const foundInList = lanhDaoBans.find((user: User) => user.id === initialData.leader_ban_id);
                if (!foundInList) {
                  // Tìm user trong danh sách tất cả users
                  const selectedUser = allUsers.find((user: User) => user.id === initialData.leader_ban_id);
                  if (selectedUser) {
                    // Thêm user này vào đầu danh sách để hiển thị
                    finalLanhDaoBanList = [selectedUser, ...lanhDaoBans];
                  }
                }
              }
            }
            
            setTruongPhongList(finalTruongPhongList);
            setLanhDaoBanList(finalLanhDaoBanList);
            
            // Đảm bảo formData được set đúng khi truongPhongList đã sẵn sàng
            if (isEdit && initialData) {
              setFormData(prev => ({
                ...prev,
                leader_phong_id: initialData.leader_phong_id || null,
                leader_ban_id: initialData.leader_ban_id || null
              }));
            }

            // Tự động chọn user hiện tại làm trưởng phòng ký nếu user đó có role TRUONG_PHONG
            // Chỉ thực hiện khi tạo mới (không phải edit) và chưa có leader_phong_id
            if (!isEdit && !formData.leader_phong_id) {
              const currentUserStr = localStorage.getItem('currentUser');
              if (currentUserStr) {
                try {
                  const currentUser = JSON.parse(currentUserStr);
                  // Tìm user hiện tại trong danh sách trưởng phòng
                  const foundTruongPhong = sortedTruongPhongs.find((user: User) => 
                    user.username === currentUser.username
                  );
                  
                  if (foundTruongPhong) {
                    setFormData(prev => ({
                      ...prev,
                      leader_phong_id: foundTruongPhong.id
                    }));
                  }
                } catch (e) {

                }
              }
            }
          }
        } catch (error) {

          enqueueSnackbar("Không thể tải danh sách lãnh đạo", { variant: 'error' });
        }
      }
    };

    fetchLeaders();
  }, [open, isEdit]);

  useEffect(() => {
    if (open && initialData) {
      setFormData({
        document_number: initialData.document_number || '',
        document_code: initialData.document_code || '',
        title: initialData.title || '',
        document_date: initialData.document_date || new Date().toISOString().split('T')[0],
        sender: initialData.sender || '',
        recipient: initialData.recipient || '',
        document_type: initialData.document_type || 'Công văn',
        priority: initialData.priority || 'Thường',
        security_level: initialData.security_level || 'Thường',
        description: initialData.description || '',
        status: initialData.status || 'moi_khoi_tao',
        tags: initialData.tags || [],
        leader_phong_id: initialData.leader_phong_id || null,
        leader_ban_id: initialData.leader_ban_id || null,
        han_xu_ly: initialData.han_xu_ly || '',
        don_vi_soan_thao: initialData.don_vi_ban_hanh || initialData.don_vi_soan_thao || '',
        xu_ly_chinh_mrb: initialData.xu_ly_chinh_mrb || '',
        phoi_hop_mrb: initialData.phoi_hop_mrb || [],
        don_vi_xu_ly: initialData.don_vi_xu_ly || [],
        don_vi_xu_ly_cp: initialData.don_vi_xu_ly_cp || [],
        don_vi_phoi_hop_chua_qlvb: initialData.don_vi_phoi_hop_chua_qlvb || [],
        ghi_chu: initialData.ghi_chu || '',
        luong_xanh: initialData.luong_xanh || initialData.luong_xanh || '',
      });
      // Gọi lại fetchSoanThaoList với đơn vị soạn thảo hiện tại
      const donVi = initialData.don_vi_ban_hanh || initialData.don_vi_soan_thao || '';
      if (donVi) {
        fetchSoanThaoList(donVi);
      }
    }
    // eslint-disable-next-line
  }, [open, initialData]);

  // Thêm useEffect này để set đơn vị soạn thảo mặc định theo đơn vị của người đăng nhập
  useEffect(() => {
    // Chỉ thực hiện khi mở dialog và là tạo mới (không phải edit)
    if (open && !isEdit) {
      // Thêm kiểm tra để chỉ chạy khi don_vi_soan_thao chưa được chọn hoặc là giá trị mặc định
      if (!formData.don_vi_soan_thao || formData.don_vi_soan_thao === 'Ban Quản lý Đường sắt đô thị') {
        const currentUserStr = localStorage.getItem('currentUser');
        if (currentUserStr) {
          try {
            const currentUser = JSON.parse(currentUserStr);
            
            // Kiểm tra xem user có trường Don_vi không
            if (currentUser.Don_vi && Array.isArray(currentUser.Don_vi) && currentUser.Don_vi.length > 0) {
              
              // Tìm đơn vị khác ngoài BAN_QL
              let preferredUnit = currentUser.Don_vi.find((unit: string) => unit !== 'BAN_QL');
              
              // Nếu không có đơn vị nào khác BAN_QL, lấy đơn vị đầu tiên
              if (!preferredUnit && currentUser.Don_vi.length > 0) {
                preferredUnit = currentUser.Don_vi[0];
              }
              
              // Map từ mã đơn vị sang tên đơn vị
              if (preferredUnit) {
                // Tạo mapping ngược từ mã đơn vị sang tên đơn vị
                const reverseUnitMapping: Record<string, string> = {};
                Object.entries(unitMapping).forEach(([name, code]) => {
                  reverseUnitMapping[code] = name;
                });
                
                // Lấy tên đơn vị từ mã
                const unitName = reverseUnitMapping[preferredUnit];;
                
                if (unitName) {
                  setFormData(prev => ({
                    ...prev,
                    don_vi_soan_thao: unitName
                  }));
                }
              }
            }
          } catch (e) {

          }
        }
      }
    }
  }, [open, isEdit]);

  // Sửa hàm handleSelectChange để khi thay đổi đơn vị soạn thảo, vẫn giữ người đăng nhập hiện tại nếu có thể
  const handleSelectChange = (field: keyof DocumentFormData) => (event: SelectChangeEvent<string>) => {
    if (field === 'don_vi_soan_thao') {
      // Lưu lại đơn vị mới được chọn
      const newDonVi = event.target.value;
      
      // Cập nhật đơn vị soạn thảo và xóa người soạn thảo hiện tại
      setFormData(prev => ({
        ...prev,
        [field]: newDonVi,
        sender: '' // Reset sender để bắt người dùng chọn lại hoặc tự động chọn nếu tìm thấy
      }));

      // Sau khi fetch danh sách người soạn thảo, kiểm tra nếu user hiện tại thuộc đơn vị này
      // thì tự động chọn user đó
      const currentUserStr = localStorage.getItem('currentUser');
      if (currentUserStr) {
        try {
          const currentUser = JSON.parse(currentUserStr);
          // Lưu username để so sánh sau khi fetch danh sách người soạn thảo
          const currentUsername = currentUser.username;
          
          // Gọi API để lấy danh sách người soạn thảo của đơn vị mới
          const unitCode = unitMapping[newDonVi as keyof typeof unitMapping];
          if (unitCode) {
            axiosInstance.post('/users/by-unit', {
              don_vi: unitCode,
              keyword: ''
            }).then(response => {
              if (response.data.code === 0) {
                const userList = response.data.data.records;
                setSoanThaoList(userList);
                
                // Tìm user hiện tại trong danh sách mới
                const foundUser = userList.find((u: User) => u.username === currentUsername);
                if (foundUser) {
                  // Nếu tìm thấy, tự động chọn user đó
                  setFormData(prev => ({
                    ...prev,
                    sender: foundUser.username
                  }));
                }
                // Không tự động chọn người đầu tiên nếu không tìm thấy người dùng hiện tại
                // Để trống để bắt người dùng chọn
              }
            }).catch(error => {

            });
          }
        } catch (e) {

        }
      }
    } else {
      setFormData({
        ...formData,
        [field]: event.target.value
      });
    }
  };

  const handleTextFieldChange = (field: keyof DocumentFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    let nextValue = event.target.value;
    if (field === 'title') {
      nextValue = nextValue.replace(/[\[\]]/g, '');
    }
    setFormData({
      ...formData,
      [field]: nextValue
    });
  };


  const handleFilesWithCategory = (files: File[], category: 'main' | 'reference') => {
    // Kiểm tra loại file được phép
    const checkFileAllowed = category === 'main' ? isMainFileAllowed : isReferenceFileAllowed;
    const allowedExtensions = category === 'main' 
      ? ALLOWED_MAIN_FILE_EXTENSIONS.join(', ')
      : ALLOWED_REFERENCE_FILE_EXTENSIONS.join(', ');
    
    // Phân loại file: loại không hợp lệ, file quá kích thước, và file hợp lệ
    const invalidTypeFiles: File[] = [];
    const oversizedFiles: File[] = [];
    const validFiles: File[] = [];
    
    files.forEach(file => {
      if (!checkFileAllowed(file.name)) {
        invalidTypeFiles.push(file);
      } else if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });

    // Thông báo các file không hợp lệ về loại
    if (invalidTypeFiles.length > 0) {
      enqueueSnackbar(
        `Các file sau không được phép upload cho ${category === 'main' ? 'file chính' : 'file liên quan'}: ${invalidTypeFiles.map(f => f.name).join(', ')}. Chỉ chấp nhận các file: ${allowedExtensions}`,
        { variant: 'error', autoHideDuration: 6000 }
      );
    }

    // Thông báo các file quá kích thước
    if (oversizedFiles.length > 0) {
      enqueueSnackbar(
        `Các file sau vượt quá giới hạn 50MB: ${oversizedFiles.map(f => f.name).join(', ')}. File lớn hơn 50MB sẽ bị loại bỏ khi đồng bộ với hệ thống Quản lý Văn bản của Thành phố`,
        { variant: 'warning', autoHideDuration: 6000 }
      );
    }

    // Nếu không có file hợp lệ, dừng xử lý
    if (validFiles.length === 0) {
      return;
    }

    // Kiểm tra số lượng file chính nếu đang upload file chính
    if (category === 'main') {
      const currentMainFilesCount = selectedFiles.filter(f => f.file_type_category === 'main').length;
      
      if (currentMainFilesCount >= MAX_MAIN_FILES) {
        enqueueSnackbar(
          `Số lượng file chính tối đa là ${MAX_MAIN_FILES} files. Vui lòng xóa một số file chính trước khi thêm mới.`,
          { variant: 'error' }
        );
        return;
      }

      // Kiểm tra nếu thêm các file hợp lệ này sẽ vượt quá giới hạn
      const totalAfterAdd = currentMainFilesCount + validFiles.length;
      if (totalAfterAdd > MAX_MAIN_FILES) {
        const allowedCount = MAX_MAIN_FILES - currentMainFilesCount;
        const filesToAdd = validFiles.slice(0, allowedCount);
        
        if (filesToAdd.length < validFiles.length) {
          enqueueSnackbar(
            `Chỉ có thể thêm ${allowedCount} file chính nữa (tổng số file chính tối đa là ${MAX_MAIN_FILES}). ${validFiles.length - allowedCount} file đã bị loại bỏ.`,
            { variant: 'warning' }
          );
        }

        // Chỉ thêm số lượng file cho phép
        const newFiles = filesToAdd.map(file => ({
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          file_path: '',
          description: '',
          file_type_category: category,
          file: file
        }));
        
        setSelectedFiles(prev => [...prev, ...newFiles]);
        return;
      }
    }

    // Thêm tất cả các file hợp lệ vào danh sách
    const newFiles = validFiles.map(file => ({
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      file_path: '',
      description: '',
      file_type_category: category,
      file: file
    }));
    
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      // Kiểm tra kích thước của từng file
      const oversizedFiles = Array.from(event.target.files).filter(file => file.size > MAX_FILE_SIZE);
      
      if (oversizedFiles.length > 0) {
        // Hiển thị thông báo cho người dùng
        enqueueSnackbar(
          `Các file sau vượt quá giới hạn 50MB: ${oversizedFiles.map(f => f.name).join(', ')}. File lớn hơn 50Mb sẽ bị loại bỏ khi đồng bộ với hệ thống Quản lý Văn bản của Thành phố`,
          { variant: 'warning', autoHideDuration: 6000 }
        );
        
        // Chỉ xử lý các file không vượt quá kích thước
        const validFiles = Array.from(event.target.files).filter(file => file.size <= MAX_FILE_SIZE);
        if (validFiles.length > 0) {
          setPendingFiles(validFiles);
          setPendingFileIndex(0);
          setShowFileTypeDialog(true);
        }
      } else {
        // Tất cả các file đều hợp lệ
        setPendingFiles(Array.from(event.target.files));
        setPendingFileIndex(0);
        setShowFileTypeDialog(true);
      }
    }
  };

  const handleSelectFileType = () => {
    if (pendingFiles.length > 0 && pendingFileIndex < pendingFiles.length) {
      const file = pendingFiles[pendingFileIndex];
      // Thêm file vào selectedFiles sau khi người dùng đã chọn loại file
      setSelectedFiles(prev => ([
        ...prev,
        {
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          file_path: '',
          description: '',
          file_type_category: 'main',
          file: file
        }
      ]));

      // Chuyển sang file tiếp theo hoặc đóng dialog
      if (pendingFileIndex + 1 < pendingFiles.length) {
        setPendingFileIndex(pendingFileIndex + 1);
      } else {
        setShowFileTypeDialog(false);
        setPendingFiles([]);
        setPendingFileIndex(0);
      }
    }
  };

  // Hàm đồng bộ bản ghi nhà thầu ban hành: thêm mới, xóa bỏ theo diff
  const syncNhaThauBanHanhRecords = async (
    idOut: number,
    flyId: number | null = null,
    selectedCodesOverride?: string[]
  ) => {
    const selectedCodes = selectedCodesOverride ?? selectedNhaThau;
    const existingCodes = existingNhaThauRecords.map(r => r.name_nha_thau);

    // Nhà thầu cần xóa: có trong existing nhưng không còn trong selected
    const toDelete = existingNhaThauRecords.filter(r => !selectedCodes.includes(r.name_nha_thau));
    // Nhà thầu cần thêm: có trong selected nhưng chưa có trong existing
    const toAdd = selectedCodes.filter(code => !existingCodes.includes(code));

    const deletePromises = toDelete.map(r =>
      axiosInstance.delete(`/tuvan-nhathau/fly-actions-nha-thau-ban-hanh/${r.id}`).catch(() => {})
    );

    const addPromises = toAdd.map(code =>
      axiosInstance.post('/tuvan-nhathau/fly-actions-nha-thau-ban-hanh', {
        fly_id: flyId,
        id_out: idOut,
        name_nha_thau: code,
        gui_mail_check: 2,
        note: null,
      }).catch(() => {})
    );

    await Promise.allSettled([...deletePromises, ...addPromises]);
  };

  // Hàm gửi thông tin văn bản dưới dạng draft
  const submitDocumentInfoAsDraft = async () => {
    // Chuẩn bị document data với status='draft'
    const documentData = {
      ...formData,
      status: 'draft', // Thêm status draft
      files: selectedFiles
        .filter(file => file.id)
        .map(file => ({
          id: file.id,
          file_name: file.file_name,
          file_type: file.file_type,
          file_size: file.file_size,
          description: file.description,
          file_path: file.file_path,
          file_type_category: file.file_type_category || 'main'
        })),
      // Thêm thông tin văn bản đến liên quan (luôn truyền mảng, có thể rỗng)
      sokyhieu_flyid_den: relatedIncomingDocs
    };

    const url = fromTask && taskId ? `/tasks/${taskId}/outgoing-documents` : '/outgoing-documents';
    const response = await axiosInstance.post(url, documentData);
    if (response.data.code !== 0) {
      throw new Error(response.data.msg || response.data.message || 'Có lỗi xảy ra khi tạo draft văn bản');
    }
    return { documentId: response.data.data?.id };
  };

  // Hàm gửi thông tin văn bản
  const submitDocumentInfo = async () => {
    // Chuẩn bị document data
    const documentData = {
      ...formData,
      files: selectedFiles
        .filter(file => file.id)
        .map(file => ({
          id: file.id,
          file_name: file.file_name,
          file_type: file.file_type,
          file_size: file.file_size,
          description: file.description,
          file_path: file.file_path,
          file_type_category: file.file_type_category || 'main'
        })),
      // Thêm thông tin văn bản đến liên quan (luôn truyền mảng, có thể rỗng)
      sokyhieu_flyid_den: relatedIncomingDocs
    };

    let response;
    if (isEdit) {
      response = await axiosInstance.put(`/outgoing-documents/${documentId}`, documentData);
      if (response.data.code !== 0) {
        throw new Error(response.data.msg || response.data.message || 'Có lỗi xảy ra khi cập nhật văn bản');
      }
      return { documentId: documentId };
    } else {
      const url = fromTask && taskId ? `/tasks/${taskId}/outgoing-documents` : '/outgoing-documents';
      response = await axiosInstance.post(url, documentData);
      if (response.data.code !== 0) {
        throw new Error(response.data.msg || response.data.message || 'Có lỗi xảy ra khi tạo văn bản');
      }
      return { documentId: response.data.data?.id };
    }
  };

  // Hàm lưu draft
  const handleSaveDraft = async () => {
    try {
      setLoading(true);
      setUploadProgress({}); // Reset upload progress

      // Validate form cơ bản (không cần file chính cho draft)
      if (!formData.don_vi_soan_thao) {
        enqueueSnackbar('Vui lòng chọn Đơn vị soạn thảo!', { variant: 'error' });
        return;
      }
      if (!formData.sender) {
        enqueueSnackbar('Vui lòng chọn Người soạn thảo!', { variant: 'error' });
        return;
      }

      // Bước 1: Gửi thông tin văn bản với status='draft'
      const { documentId: newDocumentId } = await submitDocumentInfoAsDraft();
      
      if (!newDocumentId) {
        throw new Error('Không nhận được ID văn bản sau khi tạo draft');
      }

      // Bước 2: Upload các file mới (nếu có)
      const newFiles = selectedFiles.filter(file => file.file);
      for (let i = 0; i < newFiles.length; i++) {
        const fileData = newFiles[i];
        if (fileData.file) {
          try {
            // Thông báo đang xử lý file
            enqueueSnackbar(
              `Đang xử lý file ${fileData.file_name}...`,
              { variant: 'info', autoHideDuration: 1000 }
            );

            // Chia file thành chunks
            const chunks = createChunks(fileData.file);
            const totalChunks = chunks.length;         

            // Khởi tạo progress cho file
            setUploadProgress(prev => ({
              ...prev,
              [fileData.file_name]: 0
            }));

            // Upload từng chunk
            for (let j = 0; j < totalChunks; j++) {
              const chunk = chunks[j];
              const base64Chunk = await blobToBase64(chunk);
              
              const chunkPayload = {
                document_id: newDocumentId,
                file_info: {
                  file_name: fileData.file_name,
                  file_type: fileData.file_type,
                  file_type_category: fileData.file_type_category || 'main',
                  description: fileData.description || '',
                  total_chunks: totalChunks,
                  chunk_number: j,
                  chunk_index: j + 1,
                  file_size: fileData.file.size,
                  chunk_data: base64Chunk,
                  document_info: {
                    document_id: newDocumentId,
                    document_number: formData.document_number || '',
                    title: formData.title || ''
                  }
                }
              };

              const uploadResponse = await axiosInstance.post('/outgoing-documents/upload-file', chunkPayload);
              
              if (uploadResponse.data.code !== 0) {
                throw new Error(`Lỗi upload chunk ${j + 1}/${totalChunks} của file ${fileData.file_name}: ${uploadResponse.data.msg}`);
              }

              // Cập nhật progress
              const progress = Math.round(((j + 1) / totalChunks) * 100);
              setUploadProgress(prev => ({
                ...prev,
                [fileData.file_name]: progress
              }));
            }
            
          } catch (fileError: any) {

            enqueueSnackbar(
              `Lỗi upload file ${fileData.file_name}: ${fileError.message}`,
              { variant: 'error' }
            );
            // Tiếp tục với file tiếp theo thay vì dừng toàn bộ quá trình
          }
        }
      }

      // Tạo bản ghi nhà thầu ban hành
      await syncNhaThauBanHanhRecords(newDocumentId, null);

      enqueueSnackbar('Lưu draft thành công!', { variant: 'success' });
      onSuccess();
      
    } catch (error: any) {

      // Lấy thông báo lỗi từ API response
      const errorMessage = error?.response?.data?.msg || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Có lỗi xảy ra khi lưu draft';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
      setUploadProgress({});
    }
  };

  // Function để reset form về trạng thái ban đầu
  const resetForm = () => {
    setFormData({
      document_number: '',
      document_code: '',
      title: '',
      document_date: new Date().toISOString().split('T')[0],
      sender: '',
      recipient: '',
      document_type: 'Công văn',
      priority: 'Thường',
      security_level: 'Thường',
      description: '',
      status: 'moi_khoi_tao',
      tags: [],
      leader_phong_id: null,
      leader_ban_id: null,
      han_xu_ly: '',
      don_vi_soan_thao: 'Ban Quản lý Đường sắt đô thị',
      xu_ly_chinh_mrb: '',
      phoi_hop_mrb: [],
      don_vi_xu_ly: [],
      don_vi_xu_ly_cp: [],
      don_vi_phoi_hop_chua_qlvb: [],
      ghi_chu: '',
      luong_xanh: '',
    });
    
    // Reset files
    setSelectedFiles([]);
    setPendingFiles([]);
    setPendingFileIndex(0);
    setPendingFileBase64('');
    
    // Reset related documents
    setRelatedIncomingDocs([]);
    setRelatedIncomingDocsDetails([]);

    // Reset nhà thầu ban hành
    setSelectedNhaThau([]);
    setExistingNhaThauRecords([]);
  };

  // Drag & drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setDragFiles(files);
      setShowFileTypeDialog(true);
    }
  };

  // Function để xử lý đóng dialog
  const handleClose = () => {
    // Reset form nếu không phải edit mode
    if (!isEdit) {
      resetForm();
    }
    setConfirmTrinhLanhDaoPhongOpen(false);
    setPendingDocumentId(null);
    setSendToContractorsAfterCreateOpen(false);
    setPendingDocIdForContractors(null);
    onClose();
  };

  // Hàm xử lý trình lãnh đạo phòng
  const handleTrinhLanhDaoPhong = async () => {
    if (!pendingDocumentId || !formData.leader_phong_id) {
      enqueueSnackbar('Thiếu thông tin để trình lãnh đạo phòng', { variant: 'error' });
      return;
    }

    setTrinhLanhDaoPhongLoading(true);
    try {
      const response = await axiosInstance.post('/xlvb/outgoing-documents/trinh-ld-phong', {
        doc_id: pendingDocumentId,
        user_lanhdao_phong_id: formData.leader_phong_id,
        note: null
      });

      if (response.data.code === 0) {
        enqueueSnackbar(response.data.message || 'Trình lãnh đạo phòng thành công', { 
          variant: 'success' 
        });
        
        // Reset form và đóng dialog
        resetForm();
        setConfirmTrinhLanhDaoPhongOpen(false);
        setPendingDocumentId(null);
        onSuccess();
        onClose();
      } else {
        enqueueSnackbar(response.data.message || 'Có lỗi xảy ra khi trình lãnh đạo phòng', { 
          variant: 'error' 
        });
      }
    } catch (error: any) {

      enqueueSnackbar(
        error.response?.data?.message || 'Có lỗi xảy ra khi trình lãnh đạo phòng', 
        { variant: 'error' }
      );
    } finally {
      setTrinhLanhDaoPhongLoading(false);
    }
  };

  // Hàm hủy trình lãnh đạo phòng
  const handleCancelTrinhLanhDaoPhong = () => {
    setConfirmTrinhLanhDaoPhongOpen(false);
    setPendingDocumentId(null);
    
    // Reset form và đóng dialog
    resetForm();
    onSuccess();
    onClose();
  };

  // Hàm xử lý kết thúc văn bản đến
  const handleKetThucVanBanDen = async (newDocumentId: number) => {
    try {
      setKetThucVanBanDenLoading(true);
      
      // Gọi API kết thúc từng văn bản đến
      const results = await Promise.allSettled(
        relatedIncomingDocs.map(async (doc) => {
          try {
            const response = await axiosInstance.post(`/incoming-documents/${doc.id_incoming}/close`, {
              note: `Kết thúc tự động khi tạo văn bản đi liên quan`
            });
            return { doc, success: response.data.code === 0, message: response.data.message };
          } catch (error: any) {
            return { 
              doc, 
              success: false, 
              message: error.response?.data?.message || 'Có lỗi xảy ra' 
            };
          }
        })
      );

      // Kiểm tra kết quả
      const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        enqueueSnackbar(
          `Đã kết thúc ${successCount} văn bản đến${failCount > 0 ? `, ${failCount} văn bản thất bại` : ''}`,
          { variant: successCount === results.length ? 'success' : 'warning' }
        );
      }

      if (failCount > 0) {
        results.forEach((result, index) => {
          if (result.status === 'fulfilled' && !result.value.success) {
            enqueueSnackbar(
              `Văn bản ${relatedIncomingDocs[index].so_ky_hieu_den}: ${result.value.message}`,
              { variant: 'error' }
            );
          }
        });
      }

      // Đóng modal và tiếp tục xử lý
      setConfirmKetThucVanBanDenOpen(false);
      const docId = pendingNewDocumentId;
      setPendingNewDocumentId(null);
      
      // Nếu có leader_phong_id, hiển thị modal xác nhận trình lãnh đạo phòng
      if (docId && formData.leader_phong_id) {
        setPendingDocumentId(docId);
        setConfirmTrinhLanhDaoPhongOpen(true);
      } else {
        // Reset form và đóng dialog
        resetForm();
        onSuccess();
        onClose();
      }
    } catch (error: any) {

      enqueueSnackbar(
        error.response?.data?.message || 'Có lỗi xảy ra khi kết thúc văn bản đến',
        { variant: 'error' }
      );
    } finally {
      setKetThucVanBanDenLoading(false);
    }
  };

  // Hàm hủy kết thúc văn bản đến
  const handleCancelKetThucVanBanDen = () => {
    setConfirmKetThucVanBanDenOpen(false);
    const docId = pendingNewDocumentId;
    setPendingNewDocumentId(null);
    
    // Nếu có leader_phong_id, hiển thị modal xác nhận trình lãnh đạo phòng
    if (docId && formData.leader_phong_id) {
      setPendingDocumentId(docId);
      setConfirmTrinhLanhDaoPhongOpen(true);
    } else {
      // Reset form và đóng dialog
      resetForm();
      onSuccess();
      onClose();
    }
  };

  /** Sau tạo mới/cập nhật + upload file: đồng bộ nhà thầu từ modal rồi tiếp tục luồng */
  const handleSendToContractorsAfterCreateFinish = async (codes: string[]) => {
    if (pendingDocIdForContractors == null) return;
    const newDocumentId = pendingDocIdForContractors;

    await syncNhaThauBanHanhRecords(newDocumentId, null, codes);
    setSelectedNhaThau(codes);
    setSendToContractorsAfterCreateOpen(false);
    setPendingDocIdForContractors(null);

    // Với chỉnh sửa: chốt luôn sau khi xử lý modal nhà thầu
    if (isEdit) {
      enqueueSnackbar('Cập nhật văn bản thành công', { variant: 'success' });
      onSuccess();
      onClose();
      return;
    }

    enqueueSnackbar('Thêm văn bản đi thành công', { variant: 'success' });

    if (relatedIncomingDocs.length > 0) {
      if (relatedIncomingDocsDetails.length === 0) {
        await fetchRelatedIncomingDocsDetails(relatedIncomingDocs);
      }
      setPendingNewDocumentId(newDocumentId);
      setConfirmKetThucVanBanDenOpen(true);
    } else if (formData.leader_phong_id) {
      setPendingDocumentId(newDocumentId);
      setConfirmTrinhLanhDaoPhongOpen(true);
    } else {
      resetForm();
      onSuccess();
      onClose();
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setUploadProgress({}); // Reset upload progress

      // Validate form
      if (!formData.don_vi_soan_thao) {
        enqueueSnackbar('Vui lòng chọn Đơn vị soạn thảo!', { variant: 'error' });
        return;
      }
      if (!formData.sender) {
        enqueueSnackbar('Vui lòng chọn Người soạn thảo!', { variant: 'error' });
        return;
      }
      if (!formData.leader_phong_id) {
        enqueueSnackbar('Vui lòng chọn Trưởng phòng ký!', { variant: 'error' });
        return;
      }

      // Validate file chính
      const mainFiles = selectedFiles.filter(file => file.file_type_category === 'main');
      if (mainFiles.length === 0) {
        enqueueSnackbar('Vui lòng thêm ít nhất một file chính!', { variant: 'error' });
        return;
      }
      if (mainFiles.length > MAX_MAIN_FILES) {
        enqueueSnackbar(
          `Số lượng file chính tối đa là ${MAX_MAIN_FILES} files. Hiện tại bạn có ${mainFiles.length} file chính. Vui lòng xóa bớt file chính trước khi tiếp tục.`,
          { variant: 'error' }
        );
        return;
      }

      // Bước 1: Gửi thông tin văn bản và lấy document_id
      const { documentId: newDocumentId } = await submitDocumentInfo();
      
      if (!newDocumentId) {
        throw new Error('Không nhận được ID văn bản sau khi tạo/cập nhật');
      }

      // Bước 2: Upload các file mới
      const newFiles = selectedFiles.filter(file => file.file);
      for (let i = 0; i < newFiles.length; i++) {
        const fileData = newFiles[i];
        if (fileData.file) {
          try {
            // Thông báo đang xử lý file
            enqueueSnackbar(
              `Đang xử lý file ${fileData.file_name}...`,
              { variant: 'info', autoHideDuration: 1000 }
            );

            // Chia file thành chunks
            const chunks = createChunks(fileData.file);
            const totalChunks = chunks.length;          

            // Khởi tạo progress cho file
            setUploadProgress(prev => ({
              ...prev,
              [fileData.file_name]: 0
            }));

            // Upload từng chunk
            for (let j = 0; j < chunks.length; j++) {
              const chunk = chunks[j];
              const base64Chunk = await blobToBase64(chunk);
              
              // Chuẩn bị payload cho chunk
              const chunkPayload = {
                document_id: newDocumentId,
                file_info: {
                  file_name: fileData.file_name,
                  file_type: fileData.file_type,
                  file_type_category: fileData.file_type_category || 'main',
                  description: fileData.description || '',
                  total_chunks: totalChunks,
                  chunk_number: j,
                  chunk_index: j + 1,
                  file_size: fileData.file.size,
                  chunk_data: base64Chunk,
                  document_info: {
                    document_id: newDocumentId,
                    document_code: formData.document_code,
                    document_type: formData.document_type
                  }
                }
              };

              // Gửi chunk
              const chunkResponse = await axiosInstance.post('/outgoing-documents/upload-chunk', chunkPayload);
              
              if (chunkResponse.data.code !== 0) {
                throw new Error(`Lỗi khi upload chunk ${j + 1}/${totalChunks} của file ${fileData.file_name}`);
              }

              // Cập nhật progress
              const progress = ((j + 1) / totalChunks) * 100;
              setUploadProgress(prev => ({
                ...prev,
                [fileData.file_name]: progress
              }));
            }

            // Thông báo upload file thành công
            enqueueSnackbar(
              `Upload file ${fileData.file_name} thành công`,
              { variant: 'success' }
            );

          } catch (error: any) {

            enqueueSnackbar(
              `Lỗi khi upload file ${fileData.file_name}: ${error.message}`, 
              { variant: 'error' }
            );
            throw error;
          }
        }
      }

        // Mở modal hỏi gửi cho nhà thầu nào sau khi ban hành (áp dụng cả tạo mới và cập nhật)
        setPendingDocIdForContractors(newDocumentId);
        setSendToContractorsAfterCreateOpen(true);
        return;

    } catch (error: any) {

      // Lấy thông báo lỗi từ API response
      const errorMessage = error?.response?.data?.msg || 
                          error?.response?.data?.message || 
                          error?.message || 
                          `Có lỗi xảy ra khi ${isEdit ? 'cập nhật' : 'tạo'} văn bản`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
      setUploadProgress({}); // Reset upload progress
    }
  };

  // Thêm useEffect để theo dõi sự thay đổi của soanThaoList
  useEffect(() => {
    if (soanThaoList.length > 0) {
      // Lấy thông tin user hiện tại
      const currentUserStr = localStorage.getItem('currentUser');
      if (currentUserStr && !formData.sender) {
        try {
          const currentUser = JSON.parse(currentUserStr);
          // Tìm user hiện tại trong danh sách
          const foundUser = soanThaoList.find(u => u.username === currentUser.username);
          if (foundUser) {
            // Nếu tìm thấy, tự động chọn user đó
            setFormData(prev => ({
              ...prev,
              sender: foundUser.username
            }));
          }
        } catch (e) {
          // Nếu lỗi thì bỏ qua
        }
      }
    }
  }, [soanThaoList]);

  // Ref để lưu danh sách đã fetch, tránh gọi API nhiều lần
  const fetchedDocsRef = useRef<string>('');

  // useEffect để fetch thông tin chi tiết của văn bản đến liên quan khi có initialData
  useEffect(() => {
    if (initialData && initialData.sokyhieu_flyid_den && initialData.sokyhieu_flyid_den.length > 0) {
      // Tạo key từ danh sách id_incoming để so sánh
      const relatedDocs = initialData.sokyhieu_flyid_den.map((doc: any) => ({
        id_incoming: doc.id_incoming,
        so_ky_hieu_den: doc.so_ky_hieu_den,
        flyID_den: doc.flyID_den
      }));
      
      const newKey = relatedDocs.map(doc => doc.id_incoming).sort().join(',');
      
      // Chỉ fetch nếu danh sách thực sự thay đổi
      if (fetchedDocsRef.current !== newKey) {
        fetchedDocsRef.current = newKey;
        setRelatedIncomingDocs(relatedDocs);
        fetchRelatedIncomingDocsDetails(relatedDocs);
      }
    } else if (initialData && (!initialData.sokyhieu_flyid_den || initialData.sokyhieu_flyid_den.length === 0)) {
      // Nếu không có sokyhieu_flyid_den, reset state
      if (fetchedDocsRef.current !== '') {
        fetchedDocsRef.current = '';
        setRelatedIncomingDocs([]);
        setRelatedIncomingDocsDetails([]);
      }
    }
    // Chỉ phụ thuộc vào sokyhieu_flyid_den, không phụ thuộc vào toàn bộ initialData
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.sokyhieu_flyid_den]);

  // useEffect để đọc thông tin văn bản đến liên quan từ localStorage khi dialog mở
  useEffect(() => {
    if (open && !isEdit) {
      // Kiểm tra localStorage cho thông tin pending
      const pendingData = localStorage.getItem('pendingCreateOutgoingWithRelated');
      if (pendingData) {
        try {
          const data = JSON.parse(pendingData);
          // Kiểm tra timestamp để đảm bảo dữ liệu không quá cũ (5 phút)
          const maxAge = 5 * 60 * 1000; // 5 phút
          if (Date.now() - data.timestamp < maxAge && data.relatedIncomingDocs) {
            // Set thông tin văn bản đến liên quan
            setRelatedIncomingDocs(data.relatedIncomingDocs);
            fetchRelatedIncomingDocsDetails(data.relatedIncomingDocs);
          }
          // Xóa dữ liệu sau khi đã sử dụng
          localStorage.removeItem('pendingCreateOutgoingWithRelated');
        } catch (error) {

          localStorage.removeItem('pendingCreateOutgoingWithRelated');
        }
      }
    }
  }, [open, isEdit]);

  return (
    <>
      {/* Dialog thêm VB đến liên quan - hiển thị độc lập bên ngoài */}
      <RelatedIncomingDocuments
        open={showRelatedDocsDialog}
        onClose={() => setShowRelatedDocsDialog(false)}
        documentId={documentId}
        onSelectDocuments={(selectedDocs) => {
          setRelatedIncomingDocs(selectedDocs);
          fetchRelatedIncomingDocsDetails(selectedDocs);
          setShowRelatedDocsDialog(false);
        }}
        initialSelectedDocs={relatedIncomingDocs}
      />
      
      {/* Dialog thông tin nơi nhận và xử lý */}
      <RecipientDetailsDialog
        open={showRecipientDialog}
        onClose={() => setShowRecipientDialog(false)}
        onSave={handleSaveRecipientDetails}
        initialData={{
          recipient: formData.recipient,
          xu_ly_chinh_mrb: formData.xu_ly_chinh_mrb,
          phoi_hop_mrb: formData.phoi_hop_mrb,
          don_vi_xu_ly: formData.don_vi_xu_ly,
          don_vi_xu_ly_cp: formData.don_vi_xu_ly_cp,
          don_vi_phoi_hop_chua_qlvb: formData.don_vi_phoi_hop_chua_qlvb,
          description: formData.description,
          ghi_chu: formData.ghi_chu || ''
        }}
      />

      <SendToContractorsAfterPublishModal
        open={sendToContractorsAfterCreateOpen}
        documentId={pendingDocIdForContractors}
        initialSelectedCodes={selectedNhaThau}
        onFinish={handleSendToContractorsAfterCreateFinish}
      />
      
      <Dialog open={open} onClose={handleClose} maxWidth={false} fullWidth sx={{ '& .MuiDialog-paper': { width: '1500px', maxWidth: '1500px' } }}>
        <DialogTitle
          sx={{ 
            fontSize: '14px',
            bgcolor: 'primary.main',
            color: 'white'
          }}
        >
          {isEdit ? 'Chỉnh sửa văn bản đi' : 'Tạo mới văn bản đi'}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx= {{pt:1}} alignItems="stretch">
          {/* File upload section */}
          <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                display: 'flex', 
                flexDirection: 'column', 
                flex: 1,
                border: isDragOver ? '2px dashed #1976d2' : '1px solid #e0e0e0',
                backgroundColor: isDragOver ? '#f3f8ff' : 'transparent',
                transition: 'all 0.2s ease'
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Phần trên */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2 
              }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '14px' }}>
                    Danh sách file đính kèm
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                    * Yêu cầu có ít nhất một file chính
                    {selectedFiles.length > 0 && (
                      <span style={{ marginLeft: '8px' }}>
                        {(() => {
                          const mainFiles = selectedFiles.filter(f => f.file_type_category === 'main');
                          const referenceFiles = selectedFiles.filter(f => f.file_type_category === 'reference');
                          return `(Chính: ${mainFiles.length}, Tham khảo: ${referenceFiles.length})`;
                        })()}
                      </span>
                    )}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'warning.main', fontSize: '11px', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                    ⚠️ File lớn hơn 50Mb sẽ bị loại bỏ khi đồng bộ với hệ thống Quản lý Văn bản của Thành phố
                  </Typography>
                </Box>
                <input
                  type="file"
                  multiple
                  accept=".doc,.docx,.xls,.xlsx,.pdf"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const files = Array.from(e.target.files);
                      // Tự động đặt là file chính
                      handleFilesWithCategory(files, 'main');
                      // Reset input để có thể chọn lại cùng một file
                      e.target.value = '';
                    }
                  }}
                  style={{ display: 'none' }}
                  id="file-input-main"
                />
                <input
                  type="file"
                  multiple
                  accept=".doc,.docx,.xls,.xlsx,.pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const files = Array.from(e.target.files);
                      // Tự động đặt là file tham khảo
                      handleFilesWithCategory(files, 'reference');
                      // Reset input để có thể chọn lại cùng một file
                      e.target.value = '';
                    }
                  }}
                  style={{ display: 'none' }}
                  id="file-input-reference"
                />
              </Box>
              
              {/* Phần giữa - danh sách file */}
              <Box sx={{ 
                flex: 1,
                overflow: 'auto',
                overflowX: 'hidden', // Ngăn overflow ngang
                mb: 2,
                minHeight: '300px', // Đặt chiều cao tối thiểu cho phần danh sách file
                maxHeight: 'calc(100vh - 100px)', // Giới hạn chiều cao tối đa
                width: '100%',
                maxWidth: '100%'
              }}>
                <List sx={{ width: '100%', maxWidth: '100%' }}>
                  {selectedFiles.map((file, index) => (
                    <ListItem 
                      key={file.id || index} 
                      sx={{ 
                        pr: 6,
                        overflow: 'hidden',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        '& .MuiListItemText-root': {
                          minWidth: 0,
                          flex: 1,
                          overflow: 'hidden',
                          maxWidth: '100%'
                        }
                      }}
                    >
                      <ListItemText
                        sx={{
                          minWidth: 0,
                          flex: '1 1 0%',
                          overflow: 'hidden',
                          maxWidth: '100%',
                          width: 0, // Đặt width: 0 để flex có thể shrink element
                          '& .MuiListItemText-primary': {
                            overflow: 'hidden',
                            minWidth: 0,
                            maxWidth: '100%',
                            width: '100%',
                            display: 'block'
                          }
                        }}
                        primary={
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1, 
                              minWidth: 0, 
                              width: '100%',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              boxSizing: 'border-box'
                            }}
                          >
                            <Box 
                              component={file.file_path ? 'a' : 'span'}
                              href={file.file_path ? `${FILE_BASE_URL}/${file.file_path.replace(/^\/+/,'')}` : undefined}
                              target={file.file_path ? '_blank' : undefined}
                              rel={file.file_path ? 'noopener noreferrer' : undefined}
                              sx={{
                                textDecoration: 'none',
                                color: 'inherit',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: '1 1 0%',
                                minWidth: 0,
                                maxWidth: '100%',
                                fontSize: '14px',
                                display: 'block'
                              }}
                              title={file.file_name}
                            >
                              {file.file_name}
                            </Box>
                            <Typography 
                              variant="caption" 
                              color="primary" 
                              sx={{ 
                                ml: 1,
                                flexShrink: 0,
                                flexGrow: 0,
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                display: 'inline-block'
                              }}
                            >
                              {(() => {
                                return file.file_type_category === 'main' ? 'Chính' : 
                                       file.file_type_category === 'reference' ? (
                                         <span style={{ color: '#9c27b0' }}>Tham khảo</span>
                                       ) : 'Chính';
                              })()}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            {formatFileSize(file.file_size)}
                            {loading && uploadProgress[file.file_name] !== undefined && !file.file_path && (
                              <Box sx={{ width: '100%', mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box sx={{ width: '100%', mr: 1 }}>
                                    <LinearProgress variant="determinate" value={uploadProgress[file.file_name]} />
                                  </Box>
                                  <Box sx={{ minWidth: 35 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      {`${Math.round(uploadProgress[file.file_name])}%`}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </>
                        }
                        primaryTypographyProps={{
                          sx: { fontSize: '14px' }
                        }}
                        secondaryTypographyProps={{
                          sx: { fontSize: '12px' }
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => {
                            setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
                          }}
                          size="small"
                          disabled={loading}
                        >
                          <CloseIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                  {selectedFiles.length === 0 && (
                    <Box sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      color: 'text.secondary'
                    }}>
                      <Typography variant="body2">
                        Chưa có file nào được chọn
                      </Typography>
                      <Typography variant="body2">
                        Nhấn "Thêm file chính" hoặc "Thêm file tham khảo" để tải lên
                      </Typography>
                    </Box>
                  )}
                </List>
              </Box>
              
              {/* Phần dưới cùng - nút thêm VB đến liên quan và hiển thị văn bản đã chọn */}
              <Box sx={{ 
                borderTop: '1px dashed #ccc',
                pt: 2
              }}>
                {/* Hiển thị văn bản đến liên quan đã chọn - HIỂN thị NGAY dựa trên relatedIncomingDocs */}
                {relatedIncomingDocs.length > 0 && (
                  <Box sx={{ 
                    mt: 2,
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main', fontSize: '12px' }}>
                      Văn bản đến liên quan đã chọn:
                    </Typography>
                    <Grid container spacing={0.5}>
                      {relatedIncomingDocs.map((doc, index) => {
                        // Tìm chi tiết nếu đã fetch được
                        const docDetail = relatedIncomingDocsDetails.find(d => d.id_incoming === doc.id_incoming);
                        
                        return (
                          <Grid item xs={6} key={index}>
                            <Paper variant="outlined" sx={{ p: 1, backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '11px' }}>
                                Số ký hiệu: {docDetail?.document_code || doc.so_ky_hieu_den}
                              </Typography>
                              {doc.flyID_den && (
                                <Typography variant="body2" sx={{ color: 'info.main', fontSize: '10px', mb: 0.5 }}>
                                  FlyID: {doc.flyID_den}
                                </Typography>
                              )}
                              {docDetail?.description ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '10px', lineHeight: 1.2 }}>
                                  Trích yếu: {docDetail.description}
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '10px', fontStyle: 'italic' }}>
                                  Đang tải thông tin...
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Form section */}
          <Grid item xs={12} md={7} sx={{ display: 'flex' }}>
            <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Grid container spacing={2}>
                {/* <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Số ký hiệu"
                    value={formData.document_number}
                    onChange={handleTextFieldChange('document_number')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Mã định danh"
                    value={formData.document_code}
                    onChange={handleTextFieldChange('document_code')}
                  />
                </Grid> */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Trích yếu"
                    value={formData.title}
                    onChange={handleTextFieldChange('title')}
                    onKeyDown={(e) => {
                      if (e.key === '[' || e.key === ']') {
                        e.preventDefault();
                      }
                    }}
                    autoComplete="title"
                    name="title"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Ngày văn bản"
                    value={formData.document_date}
                    onChange={handleTextFieldChange('document_date')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Hạn xử lý"
                    value={formData.han_xu_ly}
                    onChange={handleTextFieldChange('han_xu_ly')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    options={DON_VI_SOAN_THAO_OPTIONS}
                    getOptionLabel={(option) => option}
                    value={formData.don_vi_soan_thao || ''}
                    onChange={(_, newValue) => {
                      // Thay đổi đơn vị soạn thảo
                      const newDonVi = newValue || '';
                      
                      // Cập nhật đơn vị soạn thảo và xóa người soạn thảo hiện tại
                      setFormData(prev => ({
                        ...prev,
                        don_vi_soan_thao: newDonVi,
                        sender: '' // Reset sender để bắt người dùng chọn lại hoặc tự động chọn nếu tìm thấy
                      }));
                      
                      // Sau khi thay đổi đơn vị, kiểm tra nếu user hiện tại thuộc đơn vị này
                      const currentUserStr = localStorage.getItem('currentUser');
                      if (currentUserStr && newDonVi) {
                        try {
                          const currentUser = JSON.parse(currentUserStr);
                          const currentUsername = currentUser.username;
                          
                          // Gọi API để lấy danh sách người soạn thảo của đơn vị mới
                          const unitCode = unitMapping[newDonVi as keyof typeof unitMapping];
                          if (unitCode) {
                            axiosInstance.post('/users/by-unit', {
                              don_vi: unitCode,
                              keyword: ''
                            }).then(response => {
                              if (response.data.code === 0) {
                                const userList = response.data.data.records;
                                setSoanThaoList(userList);
                                
                                // Tìm user hiện tại trong danh sách mới
                                const foundUser = userList.find((u: User) => u.username === currentUsername);
                                if (foundUser) {
                                  // Nếu tìm thấy, tự động chọn user đó
                                  setFormData(prev => ({
                                    ...prev,
                                    sender: foundUser.username
                                  }));
                                }
                              }
                            }).catch(error => {

                            });
                          }
                        } catch (e) {

                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Đơn vị soạn thảo"
                        required
                        error={!formData.don_vi_soan_thao}
                        helperText={!formData.don_vi_soan_thao ? "Vui lòng chọn đơn vị soạn thảo" : undefined}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            paddingRight: '40px !important', // Leave space for clear button
                            paddingLeft: '14px !important'
                          },
                          '& .MuiInputBase-input': {
                            padding: '12px 0 !important',
                            display: 'flex',
                            alignItems: 'center'
                          },
                          '& .MuiAutocomplete-input': {
                            padding: '0 !important'
                          },
                          '& .MuiAutocomplete-endAdornment': {
                            right: '4px'
                          }
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    options={soanThaoList}
                    getOptionLabel={(option) =>
                      option.fullname && option.username
                        ? `${option.fullname} (${option.username})`
                        : option.fullname || option.username || ''
                    }
                    value={soanThaoList.find(u => u.username === formData.sender) || null}
                    onChange={(_, newValue) => {
                      setFormData({
                        ...formData,
                        sender: newValue ? newValue.username : ''
                      });
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Người soạn thảo" 
                        placeholder="Nhập để tìm kiếm" 
                        required
                        error={!formData.sender}
                        helperText={!formData.sender ? "Vui lòng chọn người soạn thảo" : ""}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            paddingRight: '40px !important',
                            paddingLeft: '14px !important'
                          },
                          '& .MuiInputBase-input': {
                            padding: '12px 0 !important',
                            display: 'flex',
                            alignItems: 'center'
                          },
                          '& .MuiAutocomplete-input': {
                            padding: '0 !important'
                          },
                          '& .MuiAutocomplete-endAdornment': {
                            right: '4px'
                          }
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.username === value.username}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    options={truongPhongList}
                    getOptionLabel={(option) =>
                      option.fullname && option.username
                        ? `${option.fullname} (${option.username})`
                        : option.fullname || option.username || ''
                    }
                    value={truongPhongList.find(u => u.id === formData.leader_phong_id) || null}
                    onChange={(_, newValue) => {
                      setFormData(prev => ({
                        ...prev,
                        leader_phong_id: newValue ? newValue.id : null
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Trưởng phòng ký" placeholder="Nhập để tìm kiếm" 
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            paddingRight: '40px !important',
                            paddingLeft: '14px !important'
                          },
                          '& .MuiInputBase-input': {
                            padding: '12px 0 !important',
                            display: 'flex',
                            alignItems: 'center'
                          },
                          '& .MuiAutocomplete-input': {
                            padding: '0 !important'
                          },
                          '& .MuiAutocomplete-endAdornment': {
                            right: '4px'
                          }
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="Không tìm thấy trưởng phòng"
                    loading={truongPhongList.length === 0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    options={lanhDaoBanList}
                    getOptionLabel={(option) =>
                      option.fullname && option.username
                        ? `${option.fullname} (${option.username})`
                        : option.fullname || option.username || ''
                    }
                    value={lanhDaoBanList.find(u => u.id === formData.leader_ban_id) || null}
                    onChange={(_, newValue) => {
                      setFormData(prev => ({
                        ...prev,
                        leader_ban_id: newValue ? newValue.id : null
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Lãnh đạo ban ký" placeholder="Nhập để tìm kiếm" 
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            paddingRight: '40px !important',
                            paddingLeft: '14px !important'
                          },
                          '& .MuiInputBase-input': {
                            padding: '12px 0 !important',
                            display: 'flex',
                            alignItems: 'center'
                          },
                          '& .MuiAutocomplete-input': {
                            padding: '0 !important'
                          },
                          '& .MuiAutocomplete-endAdornment': {
                            right: '4px'
                          }
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </Grid>
                
                {/* Loại văn bản, Độ khẩn, VB Ưu tiên */}
                <Grid item xs={4}>
                  <FormControl fullWidth sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                    },
                    '& .MuiSelect-select': {
                      padding: '8px 14px',
                    }
                  }}>
                    <InputLabel>Loại văn bản</InputLabel>
                    <Select
                      value={formData.document_type}
                      label="Loại văn bản"
                      onChange={handleSelectChange('document_type')}
                    >
                      <MenuItem value="Công văn">Công văn</MenuItem>
                      <MenuItem value="Báo cáo">Báo cáo</MenuItem> 
                      <MenuItem value="Biên bản">Biên bản</MenuItem>
                      <MenuItem value="Hướng dẫn">Hướng dẫn</MenuItem>
                      <MenuItem value="Kế hoạch">Kế hoạch</MenuItem>
                      <MenuItem value="Tham vấn xây dựng">Tham vấn xây dựng</MenuItem>
                      <MenuItem value="Quyết định">Quyết định</MenuItem>
                      <MenuItem value="Thông báo">Thông báo</MenuItem>
                      <MenuItem value="Tờ trình">Tờ trình</MenuItem>
                      <MenuItem value="Hợp đồng">Hợp đồng</MenuItem>
                      <MenuItem value="Khác">Khác</MenuItem>
                      <MenuItem value="Quyết định nhân sự">Quyết định nhân sự</MenuItem>
                      <MenuItem value="Phiếu giao việc">Phiếu giao việc</MenuItem>
                      <MenuItem value="Giấy mời">Giấy mời</MenuItem>
                      <MenuItem value="Giấy mời đến">Giấy mời đến</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Độ khẩn</InputLabel>
                    <Select
                      value={formData.priority}
                      label="Độ khẩn"
                      onChange={handleSelectChange('priority')}
                    >
                      <MenuItem value="Thường">Thường</MenuItem>
                      <MenuItem value="Khẩn">Khẩn</MenuItem>
                      <MenuItem value="Hỏa tốc">Hỏa tốc</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel id="luong-xanh-label">VB Ưu tiên</InputLabel>
                    <Select
                      labelId="luong-xanh-label"
                      value={formData.luong_xanh === 'Không' ? '' : (formData.luong_xanh || '')}
                      label="Luồng Xanh"
                      onChange={handleSelectChange('luong_xanh')}
                    >
                      <MenuItem value="">Không chọn</MenuItem>
                      <MenuItem value="LUONGXANH">Tăng GRDP trên 8%</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Nút Nơi nhận - Gộp tất cả các trường nơi nhận và xử lý */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    border: '1px solid #ddd', 
                    borderRadius: 1, 
                    p: 2,
                    bgcolor: '#f9f9f9'
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Thông tin nơi nhận và xử lý
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowRecipientDialog(true)}
                        startIcon={<EditIcon />}
                      >
                        {formData.recipient || formData.xu_ly_chinh_mrb ? 'Chỉnh sửa' : 'Thêm thông tin'}
                      </Button>
                    </Box>
                    
                    {/* Hiển thị tóm tắt thông tin */}
                    {(formData.recipient || formData.xu_ly_chinh_mrb || formData.phoi_hop_mrb.length > 0 || 
                      formData.don_vi_xu_ly.length > 0 || formData.don_vi_xu_ly_cp.length > 0 || 
                      formData.don_vi_phoi_hop_chua_qlvb.length > 0) ? (
                      <Box sx={{ mt: 2 }}>
                        {/* Đơn vị xử lý (gộp SBN, CP/BN và Phối hợp chưa QLVB) */}
                        {(formData.don_vi_xu_ly.length > 0 || formData.don_vi_xu_ly_cp.length > 0 || formData.don_vi_phoi_hop_chua_qlvb.length > 0) && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Đơn vị xử lý:</strong>{' '}
                            {formData.don_vi_xu_ly.length > 0 && (
                              <>
                                <strong>SBN ({formData.don_vi_xu_ly.length}):</strong> {
                                  formData.don_vi_xu_ly.map(text => 
                                    donViXuLyList.find(opt => opt.text === text)?.value || text
                                  ).join(', ')
                                }
                              </>
                            )}
                            {formData.don_vi_xu_ly_cp.length > 0 && (
                              <>
                                {formData.don_vi_xu_ly.length > 0 && ' | '}
                                <strong>CP/BN ({formData.don_vi_xu_ly_cp.length}):</strong> {
                                  formData.don_vi_xu_ly_cp.join(', ')
                                }
                              </>
                            )}
                            {formData.don_vi_phoi_hop_chua_qlvb.length > 0 && (
                              <>
                                {(formData.don_vi_xu_ly.length > 0 || formData.don_vi_xu_ly_cp.length > 0) && ' | '}
                                <strong>Phối hợp chưa QLVB ({formData.don_vi_phoi_hop_chua_qlvb.length}):</strong> {
                                  formData.don_vi_phoi_hop_chua_qlvb.map(item => item.don_vi).join(', ')
                                }
                              </>
                            )}
                          </Typography>
                        )}
                        
                        {/* Nơi nhận MRB (gộp xử lý chính và phối hợp) */}
                        {(formData.xu_ly_chinh_mrb || formData.phoi_hop_mrb.length > 0) && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Nơi nhận MRB:</strong>{' '}
                            {formData.xu_ly_chinh_mrb && (
                              <>
                                <strong>Xử lý chính:</strong> {
                                  noiNhanList.find(opt => opt.text === formData.xu_ly_chinh_mrb)?.value || formData.xu_ly_chinh_mrb
                                }
                              </>
                            )}
                            {formData.phoi_hop_mrb.length > 0 && (
                              <>
                                {formData.xu_ly_chinh_mrb && ' | '}
                                <strong>Phối hợp ({formData.phoi_hop_mrb.length}):</strong> {
                                  formData.phoi_hop_mrb.map(text => 
                                    noiNhanList.find(opt => opt.text === text)?.value || text
                                  ).join(', ')
                                }
                              </>
                            )}
                          </Typography>
                        )}
                        
                        {/* Nội dung - giữ nguyên */}
                        {formData.description && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Nội dung:</strong> {formData.description.substring(0, 100)}{formData.description.length > 100 ? '...' : ''}
                          </Typography>
                        )}
                        
                        {/* Ghi chú - giữ nguyên */}
                        {formData.ghi_chu && (
                          <Typography variant="body2">
                            <strong>Ghi chú:</strong> {formData.ghi_chu.substring(0, 100)}{formData.ghi_chu.length > 100 ? '...' : ''}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                        Chưa có thông tin. Nhấn "Thêm thông tin" để điền.
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Nhà thầu ban hành */}
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    multiple
                    options={donViList.filter(dv => dv.Don_vi_cap === 1)}
                    getOptionLabel={(option) => `${option.code} - ${option.name}`}
                    value={donViList.filter(dv => dv.Don_vi_cap === 1 && selectedNhaThau.includes(dv.code))}
                    onChange={(_, newValue) => {
                      setSelectedNhaThau(newValue.map(dv => dv.code));
                    }}
                    renderInput={(params) => (
                      <TextField {...params}
                        label="Nhà thầu ban hành"
                        placeholder="Chọn nhà thầu..."
                        helperText="Nếu bạn ban hành cho nhà thầu, cần chọn nhà thầu cần ban hành"
                        sx={{
                          '& .MuiInputBase-root': { height: 'auto' },
                          '& .MuiInputBase-input::placeholder': { color: '#2e7d32', opacity: 0.8 },
                          '& .MuiFormHelperText-root': {
                            color: '#e65100',
                            fontStyle: 'italic',
                            animation: 'blink 1.5s ease-in-out infinite',
                            '@keyframes blink': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.3 },
                            },
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                            {option.code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                            {option.name}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    noOptionsText="Không tìm thấy đơn vị"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Thêm mới file chính, là file sẽ sử dụng để ký số" placement="top">
            <Button
              variant="outlined"
              size="small"
              onClick={() => document.getElementById('file-input-main')?.click()}
              startIcon={<AddIcon />}
              disabled={loading}
              color="primary"
              sx={{ fontSize: '13px' }}
            >
              File chính ký số
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            onClick={() => document.getElementById('file-input-reference')?.click()}
            startIcon={<AddIcon />}
            disabled={loading}
            color="secondary"
            sx={{ fontSize: '13px' }}
          >
            File đính kèm (không ký số)
          </Button>
          <Tooltip title="Thêm văn bản đến liên quan" placement="top">
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowRelatedDocsDialog(true)}
              startIcon={<AddIcon />}
              disabled={loading}
              color="info"
              sx={{ fontSize: '13px' }}
            >
              Văn bản đến liên quan {relatedIncomingDocs.length > 0 ? `(${relatedIncomingDocs.length})` : ''}
            </Button>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* {!isEdit && (
            <Button
              onClick={handleSaveDraft}
              variant="outlined"
              disabled={loading}
              sx={{ 
                borderColor: '#086428',
                color: '#086428',
                '&:hover': {
                  borderColor: '#064d1f',
                  backgroundColor: 'rgba(8, 100, 40, 0.04)'
                }
              }}
            >
              Lưu để theo dõi
            </Button>
          )} */}
          <Button onClick={handleClose}>Hủy</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {isEdit ? 'Cập nhật văn bản' : 'Tạo mới'}
          </Button>
        </Box>
      </DialogActions>
      {/* Dialog hỏi loại file khi drag & drop */}
      <Dialog open={showFileTypeDialog} onClose={() => setShowFileTypeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Chọn loại file</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Bạn muốn thêm {dragFiles.length} file(s) làm file gì?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                // Xử lý thêm file chính - sử dụng hàm handleFilesWithCategory để đảm bảo validation
                handleFilesWithCategory(dragFiles, 'main');
                setShowFileTypeDialog(false);
                setDragFiles([]);
              }}
              fullWidth
            >
              File chính
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                // Xử lý thêm file tham khảo - sử dụng hàm handleFilesWithCategory để đảm bảo validation
                handleFilesWithCategory(dragFiles, 'reference');
                setShowFileTypeDialog(false);
                setDragFiles([]);
              }}
              fullWidth
            >
              File tham khảo
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFileTypeDialog(false)}>Hủy</Button>
        </DialogActions>
      </Dialog>

      {/* Modal xác nhận kết thúc văn bản đến */}
      <Dialog
        open={confirmKetThucVanBanDenOpen}
        onClose={handleCancelKetThucVanBanDen}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Kết thúc văn bản đến liên quan
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Văn bản đi đã được tạo thành công. Bạn có muốn kết thúc các văn bản đến liên quan sau đây không?
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {relatedIncomingDocsDetails.length > 0 ? (
              relatedIncomingDocsDetails.map((doc, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {doc.document_code || doc.so_ky_hieu_den}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Số ký hiệu:</strong> {doc.so_ky_hieu_den}
                  </Typography>
                  {doc.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Trích yếu:</strong> {doc.description}
                    </Typography>
                  )}
                </Paper>
              ))
            ) : (
              relatedIncomingDocs.map((doc, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {doc.so_ky_hieu_den}
                  </Typography>
                </Paper>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelKetThucVanBanDen} 
            disabled={ketThucVanBanDenLoading}
          >
            Bỏ qua
          </Button>
          <Button
            onClick={() => {
              if (pendingNewDocumentId) {
                handleKetThucVanBanDen(pendingNewDocumentId);
              }
            }}
            variant="contained"
            disabled={ketThucVanBanDenLoading || !pendingNewDocumentId}
          >
            {ketThucVanBanDenLoading ? <CircularProgress size={20} /> : 'Kết thúc văn bản đến'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal xác nhận trình lãnh đạo phòng */}
      <Dialog
        open={confirmTrinhLanhDaoPhongOpen}
        onClose={handleCancelTrinhLanhDaoPhong}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#086428',
          color: 'white',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '51px',
          padding: '12px 16px',
          boxSizing: 'border-box'
        }}>
          Xác nhận trình lãnh đạo phòng
          <IconButton
            aria-label="close"
            onClick={handleCancelTrinhLanhDaoPhong}
            disabled={trinhLanhDaoPhongLoading}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Bạn có muốn trình lãnh đạo phòng luôn không?
          </Typography>
          {formData.leader_phong_id && (
            <Typography variant="body2" color="text.secondary">
              Lãnh đạo phòng: {truongPhongList.find(u => u.id === formData.leader_phong_id)?.fullname || `ID: ${formData.leader_phong_id}`}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 2, 
          borderTop: 1, 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          <Button
            onClick={handleCancelTrinhLanhDaoPhong}
            variant="outlined"
            disabled={trinhLanhDaoPhongLoading}
            sx={{
              fontSize: '13px',
              borderColor: '#ccc',
              color: '#666',
              '&:hover': {
                borderColor: '#999',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Không
          </Button>
          <Button
            onClick={handleTrinhLanhDaoPhong}
            variant="contained"
            disabled={trinhLanhDaoPhongLoading}
            sx={{
              fontSize: '13px',
              backgroundColor: '#086428',
              '&:hover': {
                backgroundColor: '#064e1a'
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999'
              }
            }}
          >
            {trinhLanhDaoPhongLoading ? (
              <>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Đang xử lý...
              </>
            ) : (
              'Có, trình lãnh đạo phòng'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog thêm VB đến liên quan đã được chuyển ra bên ngoài */}
    </Dialog>
  </>
  );
};

export default CreateDocumentDialog; 